import { z } from "zod";

// ============================================================================
// COMMON TYPES
// ============================================================================
// Shared base types and response type definitions used by both persisted
// and resolved formats.

// Base types
export const HashSchema = z.string();
export const FeelPredicateSchema = z.string();
export const LocalizedTextSchema = z.string();

export const ValidationRuleSchema = z.object({
  predicate: FeelPredicateSchema,
  message: LocalizedTextSchema,
});

// Visibility schema - shared across QuestionRef and Part
export const VisibilitySchema = z.union([
  z.literal("visible"),
  z.object({ visibleWhen: FeelPredicateSchema }),
]);

// Requirement schema - shared across QuestionRef
export const RequirementSchema = z.union([
  z.literal("optional"),
  z.literal("required"),
  z.object({ requiredWhen: FeelPredicateSchema }),
]);

// Response Types
export const SelectResponseTypeSchema = z.object({
  _type: z.literal("select"),
  variants: z.array(z.string()),
});

export const SelectOrOtherResponseTypeSchema = z.object({
  _type: z.literal("select-or-other"),
  variants: z.array(z.string()),
});

export const MultiSelectResponseTypeSchema = z.object({
  _type: z.literal("multi-select"),
  variants: z.array(z.string()),
});

export const MultiSelectGroupedResponseTypeSchema = z.object({
  _type: z.literal("multi-select-grouped"),
  groups: z.array(
    z.object({
      label: z.string(),
      variants: z.array(z.string()),
    })
  ),
});

export const CheckboxResponseTypeSchema = z.object({
  _type: z.literal("checkbox"),
});

export const NumberRangeResponseTypeSchema = z.object({
  _type: z.literal("number-range"),
  min: z.number(),
  max: z.number(),
});

export const IntegerResponseTypeSchema = z.object({
  _type: z.literal("integer"),
  min: z.number(),
  max: z.number(),
});

export const VasResponseTypeSchema = z.object({
  _type: z.literal("vas"),
  min: z.number(),
  max: z.number(),
});

export const DateResponseTypeSchema = z.object({
  _type: z.literal("date"),
});

export const TextResponseTypeSchema = z.object({
  _type: z.literal("text"),
  minLength: z.number().optional(),
  maxLength: z.number().optional(),
  multiline: z.boolean(),
});

export const QuestionResponseTypeSchema = z.discriminatedUnion("_type", [
  SelectResponseTypeSchema,
  SelectOrOtherResponseTypeSchema,
  MultiSelectResponseTypeSchema,
  MultiSelectGroupedResponseTypeSchema,
  CheckboxResponseTypeSchema,
  NumberRangeResponseTypeSchema,
  IntegerResponseTypeSchema,
  VasResponseTypeSchema,
  DateResponseTypeSchema,
  TextResponseTypeSchema,
]);

// Question
// Note: Question.hash is computed from all fields except id, hash, and updatedAt
// Note: Questions have no requirement field - that only exists in QuestionRef
export const QuestionSchema = z.object({
  id: z.number(),
  hash: HashSchema,
  question: LocalizedTextSchema,
  description: LocalizedTextSchema.optional(),
  responseType: QuestionResponseTypeSchema,
  semanticKey: z.string().optional(),
  updatedAt: z.string(),
});

// QuestionRef - wrapper that adds contextual conditional logic to a question
// Used in both QuestionGroups and Book Parts
export const QuestionRefSchema = z.object({
  questionId: z.number(),
  visibility: VisibilitySchema,
  requirement: RequirementSchema,
});

export type QuestionRef = z.infer<typeof QuestionRefSchema>;

// Export inferred types
export type Hash = z.infer<typeof HashSchema>;
export type FeelPredicate = z.infer<typeof FeelPredicateSchema>;
export type LocalizedText = z.infer<typeof LocalizedTextSchema>;
export type ValidationRule = z.infer<typeof ValidationRuleSchema>;
export type Visibility = z.infer<typeof VisibilitySchema>;
export type Requirement = z.infer<typeof RequirementSchema>;
export type SelectResponseType = z.infer<typeof SelectResponseTypeSchema>;
export type SelectOrOtherResponseType = z.infer<typeof SelectOrOtherResponseTypeSchema>;
export type MultiSelectResponseType = z.infer<typeof MultiSelectResponseTypeSchema>;
export type MultiSelectGroupedResponseType = z.infer<typeof MultiSelectGroupedResponseTypeSchema>;
export type CheckboxResponseType = z.infer<typeof CheckboxResponseTypeSchema>;
export type NumberRangeResponseType = z.infer<typeof NumberRangeResponseTypeSchema>;
export type IntegerResponseType = z.infer<typeof IntegerResponseTypeSchema>;
export type VasResponseType = z.infer<typeof VasResponseTypeSchema>;
export type DateResponseType = z.infer<typeof DateResponseTypeSchema>;
export type TextResponseType = z.infer<typeof TextResponseTypeSchema>;
export type QuestionResponseType = z.infer<typeof QuestionResponseTypeSchema>;
export type Question = z.infer<typeof QuestionSchema>;
